/**
 * 2D SVG floor plan renderer for IC RoomPlan JSON captures.
 *
 * Supports both formats:
 *  - Simplified (_0001): position=[world_x,world_y,world_z], no rotation
 *  - Raw Apple (_0004): transform=16-element column-major 4×4, category/confidence as {key:{}}
 *
 * ARKit Y-up: x=right, y=up, z=toward viewer.
 * Plan view: SVG_x=world_x, SVG_y=-world_z.
 *
 * Floor polygonCorners are stored in the floor's LOCAL coordinate space where
 * local_Y ≈ −world_Z (floor lies in the horizontal plane with local Z = world Y).
 * So: SVG_x=corner[0], SVG_y=−corner[1].
 */

type Pt2 = [number, number]; // [SVG_x, SVG_y]
type Mat4 = number[]; // 16-element column-major 4×4

export interface IcRoomPlanJson {
  floors: Array<{
    polygonCorners: [number, number, number][];
    dimensions: [number, number, number];
    position?: [number, number, number];
    transform?: Mat4;
  }>;
  walls: Array<{
    id?: string;
    identifier?: string;
    position?: [number, number, number];
    dimensions: [number, number, number];
    transform?: Mat4;
  }>;
  doors: Array<{
    id?: string;
    identifier?: string;
    position?: [number, number, number];
    dimensions: [number, number, number];
    category: string | Record<string, unknown>;
    transform?: Mat4;
  }>;
  windows: Array<{
    id?: string;
    identifier?: string;
    position?: [number, number, number];
    dimensions: [number, number, number];
    transform?: Mat4;
  }>;
  openings: Array<{
    id?: string;
    identifier?: string;
    position?: [number, number, number];
    dimensions: [number, number, number];
    transform?: Mat4;
  }>;
  objects: Array<{
    id?: string;
    identifier?: string;
    category: string | Record<string, unknown>;
    position?: [number, number, number];
    dimensions: [number, number, number];
    confidence: string | Record<string, unknown>;
    transform?: Mat4;
  }>;
  sections?: Array<{ center: [number, number, number]; label: string; story?: number }>;
}

type WithPosOrTransform = { position?: [number,number,number]; transform?: Mat4 };

// Returns [SVG_x, SVG_y] = [world_x, -world_z].
// transform col3 = [t12, t13, t14] = world translation; position = [world_x, world_y, world_z].
function svgPos(el: WithPosOrTransform): Pt2 | null {
  if (el.transform) return [el.transform[12], -el.transform[14]];
  if (el.position)  return [el.position[0],   -el.position[2]];
  return null;
}

// ── geometry helpers ──────────────────────────────────────────────────────────

function dist2seg(pt: Pt2, a: Pt2, b: Pt2): { dist: number; t: number } {
  const dx = b[0] - a[0], dz = b[1] - a[1];
  const len2 = dx * dx + dz * dz;
  if (len2 < 1e-12) return { dist: Math.hypot(pt[0] - a[0], pt[1] - a[1]), t: 0 };
  const t = Math.max(0, Math.min(1, ((pt[0] - a[0]) * dx + (pt[1] - a[1]) * dz) / len2));
  return { dist: Math.hypot(pt[0] - (a[0] + t*dx), pt[1] - (a[1] + t*dz)), t };
}

function closestEdge(pt: Pt2, corners: Pt2[]): { idx: number; t: number; dist: number } {
  let best = { idx: 0, t: 0, dist: Infinity };
  for (let i = 0; i < corners.length; i++) {
    const { dist, t } = dist2seg(pt, corners[i], corners[(i + 1) % corners.length]);
    if (dist < best.dist) best = { idx: i, t, dist };
  }
  return best;
}

function edgeLen(corners: Pt2[], i: number): number {
  const a = corners[i], b = corners[(i + 1) % corners.length];
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

// ── SVG helpers ───────────────────────────────────────────────────────────────

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K, attrs: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS("http://www.w3.org/2000/svg", tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

// ── main export ───────────────────────────────────────────────────────────────

export function renderRoomPlanJson2D(container: HTMLElement, data: IcRoomPlanJson): void {
  const floor = data.floors[0];
  if (!floor?.polygonCorners?.length) {
    container.textContent = "No floor polygon data";
    return;
  }

  // Floor polygonCorners are in local floor space: [local_x, local_y, local_z≈0].
  // local_x ≈ world_x, local_y ≈ −world_z → SVG: [corner[0], −corner[1]].
  const corners: Pt2[] = floor.polygonCorners.map(([x, y]) => [x, -y]);

  // Bounding box over all geometry
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  const expandBounds = ([x, z]: Pt2) => {
    minX = Math.min(minX, x); maxX = Math.max(maxX, x);
    minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z);
  };
  corners.forEach(expandBounds);
  [...data.doors, ...data.windows, ...data.openings]
    .map(svgPos).filter(Boolean).forEach(p => expandBounds(p!));

  const PAD = 0.8;
  minX -= PAD; maxX += PAD; minZ -= PAD; maxZ += PAD;
  const worldW = maxX - minX, worldH = maxZ - minZ;

  const SVG_W = 600, SVG_H = Math.round(SVG_W * worldH / worldW);
  const sx = (x: number) => ((x - minX) / worldW) * SVG_W;
  const sz = (z: number) => ((z - minZ) / worldH) * SVG_H;
  const sm = (m: number) => (m / worldW) * SVG_W;

  const WALL_W   = sm(0.12);
  const OPENING_W = sm(0.06);

  // ── Per-edge opening lists ────────────────────────────────────────────────
  type EdgeOpening = { t: number; halfFrac: number; color: string };
  const edgeOpenings = new Map<number, EdgeOpening[]>();

  const addOpening = (el: WithPosOrTransform & { dimensions: [number,number,number] }, color: string) => {
    const pt = svgPos(el);
    if (!pt) return;
    const { idx, t } = closestEdge(pt, corners);
    const len = edgeLen(corners, idx);
    if (len < 0.01) return;
    const halfFrac = (el.dimensions[0] / 2) / len;
    if (!edgeOpenings.has(idx)) edgeOpenings.set(idx, []);
    edgeOpenings.get(idx)!.push({ t, halfFrac, color });
  };

  for (const d of data.doors)    addOpening(d, "#f0963a");
  for (const w of data.windows)  addOpening(w, "#5bc8e8");
  for (const op of data.openings) addOpening(op, "#cccccc");

  // ── SVG root ──────────────────────────────────────────────────────────────
  const svg = svgEl("svg", {
    width: "100%", height: "100%",
    viewBox: `0 0 ${SVG_W} ${SVG_H}`,
    preserveAspectRatio: "xMidYMid meet",
  });
  svg.style.cssText = "display:block;max-width:100%;max-height:100%;";

  // Floor fill
  svg.appendChild(svgEl("polygon", {
    points: corners.map(([x, z]) => `${sx(x)},${sz(z)}`).join(" "),
    fill: "rgba(80,100,130,0.25)", stroke: "none",
  }));

  // ── Walls with opening gaps ───────────────────────────────────────────────
  const defs = svgEl("defs", {});
  svg.appendChild(defs);

  const hatch = svgEl("pattern", {
    id: "win-hatch", patternUnits: "userSpaceOnUse",
    width: "4", height: "4", patternTransform: "rotate(45)",
  });
  hatch.appendChild(svgEl("line", { x1: "0", y1: "0", x2: "0", y2: "4",
    stroke: "#5bc8e8", "stroke-width": "1.5" }));
  defs.appendChild(hatch);

  for (let i = 0; i < corners.length; i++) {
    const a = corners[i], b = corners[(i + 1) % corners.length];
    const ax = sx(a[0]), az = sz(a[1]), bx = sx(b[0]), bz = sz(b[1]);
    const openings = (edgeOpenings.get(i) ?? []).sort((p, q) => p.t - q.t);
    const lerp = (t: number): [number, number] => [ax + t*(bx-ax), az + t*(bz-az)];

    const solid: Array<[number, number]> = [];
    let cursor = 0;
    for (const op of openings) {
      const t0 = Math.max(0, op.t - op.halfFrac);
      const t1 = Math.min(1, op.t + op.halfFrac);
      if (t0 > cursor) solid.push([cursor, t0]);
      cursor = t1;
    }
    if (cursor < 1) solid.push([cursor, 1]);

    for (const [t0, t1] of solid) {
      const [x0, z0] = lerp(t0), [x1, z1] = lerp(t1);
      svg.appendChild(svgEl("line", {
        x1: x0, y1: z0, x2: x1, y2: z1,
        stroke: "#e8eaf0", "stroke-width": WALL_W, "stroke-linecap": "square",
      }));
    }

    for (const op of openings) {
      const t0 = Math.max(0, op.t - op.halfFrac);
      const t1 = Math.min(1, op.t + op.halfFrac);
      const [x0, z0] = lerp(t0), [x1, z1] = lerp(t1);
      if (op.color === "#5bc8e8") {
        svg.appendChild(svgEl("line", {
          x1: x0, y1: z0, x2: x1, y2: z1,
          stroke: "url(#win-hatch)", "stroke-width": WALL_W * 1.4,
        }));
      }
      svg.appendChild(svgEl("line", {
        x1: x0, y1: z0, x2: x1, y2: z1,
        stroke: op.color, "stroke-width": OPENING_W,
      }));
    }
  }

  // ── Section labels ────────────────────────────────────────────────────────
  for (const sec of data.sections ?? []) {
    const cx = sx(sec.center[0]);
    const cz = sz(-sec.center[2]);
    const fontSize = Math.max(9, sm(0.28));
    const txt = svgEl("text", {
      x: cx, y: cz,
      "text-anchor": "middle",
      "dominant-baseline": "middle",
      "font-family": "system-ui,sans-serif",
      "font-size": fontSize,
      "font-weight": "700",
      fill: "rgba(200,210,230,0.5)",
      "pointer-events": "none",
    });
    txt.textContent = sec.label;
    svg.appendChild(txt);
  }

  container.style.cssText =
    "width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#0d0f12;overflow:hidden;";
  container.innerHTML = "";
  container.appendChild(svg);
}
